import React, { useContext, useState, useRef, useEffect, useMemo, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';
import { UserContext } from '../../context/UserContext';
import { useMapContext } from '../../context/MapContext';
import AddressSearchInput from '../AddressSearch-Selector';
import './FilterControls.css'; // Use its own stylesheet
import { PropertyTypeOptions, PropertyStatusOptions, PropertyStatusNumeric } from '../../utils/enums';
import { states as usStates } from '../../utils/states';
import { formatCurrencyForDisplay, parseCurrency } from '../../utils/currencyFormatter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartSolid, faHome as faHomeSolid } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular, faHome as faHomeRegular } from '@fortawesome/free-regular-svg-icons';

const MAP_PAGE_PATH = '/map';
const PROPERTIES_PAGE_PATH = '/properties';

const FilterControls = forwardRef(({ onLocationSelect, layout = 'horizontal' }, ref) => {
  const { user } = useContext(UserContext);
  const { filters, setFilters, selectMapLocation } = useMapContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const lang = useLanguage();

  const priceDropdownRef = useRef(null);
  const bedsDropdownRef = useRef(null);
  const propertyTypeDropdownRef = useRef(null);
  const yearBuiltDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const propertyIDDropdownRef = useRef(null);
  const sellerUsernameDropdownRef = useRef(null);
  const stateDropdownRef = useRef(null);
  const stateListRef = useRef(null); // For scrolling

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [tempPriceMin, setTempPriceMin] = useState('');
  const [tempPriceMax, setTempPriceMax] = useState('');
  const [priceFilterError, setPriceFilterError] = useState('');
  const [tempBedMin, setTempBedMin] = useState('');
  const [tempBedMax, setTempBedMax] = useState('');
  const [bedFilterError, setBedFilterError] = useState('');
  const [tempSelectedTypes, setTempSelectedTypes] = useState([]);
  const [tempYearBuiltMin, setTempYearBuiltMin] = useState('');
  const [tempYearBuiltMax, setTempYearBuiltMax] = useState('');
  const [yearBuiltFilterError, setYearBuiltFilterError] = useState('');
  const [tempSelectedStatuses, setTempSelectedStatuses] = useState([]);
  const [tempPropertyID, setTempPropertyID] = useState('');
  const [tempSellerUsername, setTempSellerUsername] = useState('');
  const [tempSelectedState, setTempSelectedState] = useState('');

  const showMapControls = location.pathname.endsWith(MAP_PAGE_PATH);
  const showPropertiesControls = location.pathname.endsWith(PROPERTIES_PAGE_PATH);
  const searchParams = new URLSearchParams(location.search);
  const isSavedView = showMapControls && searchParams.get('show') === 'saved';
  const isMyPropertiesView = showMapControls && searchParams.get('view') === 'my';

  const isAnyFilterActive = useMemo(() => {
    const currentParams = new URLSearchParams(location.search);
    return (
      currentParams.get('view') === 'my' ||
      currentParams.get('show') === 'saved' ||
      filters.state ||
      filters.price?.$gte ||
      filters.price?.$lte ||
      filters.beds?.$gte ||
      filters.beds?.$lte ||
      (filters.type && filters.type.length > 0) ||
      filters.yearBuilt?.$gte ||
      filters.yearBuilt?.$lte ||
      (filters.status && filters.status.length > 0) ||
      filters.propertyID ||
      filters.sellerUsername
    );
  }, [location.search, filters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown) {
        if (activeDropdown === 'price' && priceDropdownRef.current && !priceDropdownRef.current.contains(event.target)) {
          setActiveDropdown(null);
          setTempPriceMin(filters.price?.$gte || '');
          setTempPriceMax(filters.price?.$lte || '');
        } else if (activeDropdown === 'beds' && bedsDropdownRef.current && !bedsDropdownRef.current.contains(event.target)) {
          setActiveDropdown(null);
          setTempBedMin(filters.beds?.$gte || '');
          setTempBedMax(filters.beds?.$lte || '');
        } else if (activeDropdown === 'propertyType' && propertyTypeDropdownRef.current && !propertyTypeDropdownRef.current.contains(event.target)) {
          setActiveDropdown(null);
          setTempSelectedTypes(filters.type || []);
        } else if (activeDropdown === 'yearBuilt' && yearBuiltDropdownRef.current && !yearBuiltDropdownRef.current.contains(event.target)) {
          setActiveDropdown(null);
          setTempYearBuiltMin(filters.yearBuilt?.$gte || '');
          setTempYearBuiltMax(filters.yearBuilt?.$lte || '');
        } else if (activeDropdown === 'status' && statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
          setActiveDropdown(null);
          setTempSelectedStatuses(filters.status || []);
        } else if (activeDropdown === 'propertyID' && propertyIDDropdownRef.current && !propertyIDDropdownRef.current.contains(event.target)) {
          setActiveDropdown(null);
          setTempPropertyID(filters.propertyID || '');
        } else if (activeDropdown === 'sellerUsername' && sellerUsernameDropdownRef.current && !sellerUsernameDropdownRef.current.contains(event.target)) {
          setActiveDropdown(null);
          setTempSellerUsername(filters.sellerUsername || '');
        } else if (activeDropdown === 'state' && stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
          setActiveDropdown(null);
          setTempSelectedState(filters.state || '');
        }
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown, filters]);

  useEffect(() => {
    const handleStateKeyDown = (event) => {
      if (event.key.length === 1 && event.key.match(/[a-z]/i)) {
        event.preventDefault();
        const letter = event.key.toLowerCase();
        const stateIndex = usStates.findIndex((state) => state.label.toLowerCase().startsWith(letter));

        if (stateIndex !== -1 && stateListRef.current) {
          const stateElement = stateListRef.current.children[stateIndex];
          if (stateElement) {
            stateElement.scrollIntoView({ block: 'start' });
          }
        }
      }
    };

    if (activeDropdown === 'state') document.addEventListener('keydown', handleStateKeyDown);
    return () => document.removeEventListener('keydown', handleStateKeyDown);
  }, [activeDropdown]);

  const toggleFilterDropdown = (dropdownName) => {
    if (activeDropdown === dropdownName) {
      setActiveDropdown(null);
      if (dropdownName === 'price') {
        setTempPriceMin(filters.price?.$gte || '');
        setTempPriceMax(filters.price?.$lte || '');
      } else if (dropdownName === 'beds') {
        setTempBedMin(filters.beds?.$gte || '');
        setTempBedMax(filters.beds?.$lte || '');
      } else if (dropdownName === 'propertyType') {
        setTempSelectedTypes(filters.type || []);
      } else if (dropdownName === 'yearBuilt') {
        setTempYearBuiltMin(filters.yearBuilt?.$gte || '');
        setTempYearBuiltMax(filters.yearBuilt?.$lte || '');
      } else if (dropdownName === 'status') {
        setTempSelectedStatuses(filters.status || []);
      } else if (dropdownName === 'state') {
        setTempSelectedState(filters.state || '');
      } else if (dropdownName === 'propertyID') {
        setTempPropertyID(filters.propertyID || '');
      } else if (dropdownName === 'sellerUsername') {
        setTempSellerUsername(filters.sellerUsername || '');
      }
    } else {
      setActiveDropdown(dropdownName);
      if (dropdownName === 'price') {
        setTempPriceMin(filters.price?.$gte || '');
        setTempPriceMax(filters.price?.$lte || '');
        setPriceFilterError('');
      } else if (dropdownName === 'beds') {
        setTempBedMin(filters.beds?.$gte || '');
        setTempBedMax(filters.beds?.$lte || '');
        setBedFilterError('');
      } else if (dropdownName === 'propertyType') {
        setTempSelectedTypes(filters.type || []);
      } else if (dropdownName === 'yearBuilt') {
        setTempYearBuiltMin(filters.yearBuilt?.$gte || '');
        setTempYearBuiltMax(filters.yearBuilt?.$lte || '');
        setYearBuiltFilterError('');
      } else if (dropdownName === 'status') {
        setTempSelectedStatuses(filters.status || []);
      } else if (dropdownName === 'state') {
        setTempSelectedState(filters.state || '');
      } else if (dropdownName === 'propertyID') {
        setTempPropertyID(filters.propertyID || '');
      } else if (dropdownName === 'sellerUsername') {
        setTempSellerUsername(filters.sellerUsername || '');
      }
    }
  };

  const handleSavedHomesToggle = () => {
    if (isSavedView) {
      navigate(`/${lang}${MAP_PAGE_PATH}`);
    } else {
      navigate(`/${lang}${MAP_PAGE_PATH}?show=saved`);
    }
  };

  const handleMyPropertiesToggle = () => {
    if (isMyPropertiesView) {
      navigate(`/${lang}${MAP_PAGE_PATH}`);
    } else {
      navigate(`/${lang}${MAP_PAGE_PATH}?view=my`);
    }
  };

  const handleClearAllFilters = useCallback(() => {
    setFilters({
      price: {},
      beds: {},
      type: [],
      yearBuilt: {},
      status: [],
      state: '',
      propertyID: '',
      sellerUsername: '',
    });
    setTempPriceMin('');
    setTempPriceMax('');
    setTempBedMin('');
    setTempBedMax('');
    setTempSelectedTypes([]);
    setTempYearBuiltMin('');
    setTempYearBuiltMax('');
    setTempSelectedStatuses([]);
    setTempPropertyID('');
    setTempSellerUsername('');
    setTempSelectedState('');
    selectMapLocation('reset');
    if (location.pathname.endsWith(MAP_PAGE_PATH)) {
      navigate(`/${lang}${MAP_PAGE_PATH}`, { replace: true });
    }
  }, [setFilters, selectMapLocation, location.pathname, navigate, lang]);

  useImperativeHandle(ref, () => ({
    clear: handleClearAllFilters,
  }));

  const applyPriceFilter = () => {
    const priceFilterObject = {};
    if (tempPriceMin) priceFilterObject.$gte = tempPriceMin;
    if (tempPriceMax) priceFilterObject.$lte = tempPriceMax;

    const min = parseFloat(tempPriceMin);
    const max = parseFloat(tempPriceMax);

    if (tempPriceMin && tempPriceMax && !isNaN(min) && !isNaN(max) && max < min) {
      setPriceFilterError('Max price must be greater than or equal to Min price.');
      return;
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      price: priceFilterObject,
    }));
    setPriceFilterError('');
    setActiveDropdown(null);
  };

  const clearPriceFilter = () => {
    setTempPriceMin('');
    setTempPriceMax('');
    setPriceFilterError('');
    setFilters((prevFilters) => ({
      ...prevFilters,
      price: {},
    }));
  };

  const handlePriceInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyPriceFilter();
    }
  };

  const handlePriceInputChange = (e, fieldSetter) => {
    const rawValue = parseCurrency(e.target.value);
    fieldSetter(String(rawValue));
    setPriceFilterError('');
  };

  const getFormattedPriceForInput = (rawValue) => {
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      return '';
    }
    const numericValue = Number(rawValue);
    if (isNaN(numericValue)) {
      return '';
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(numericValue);
  };

  const getPriceButtonLabel = () => {
    const minPrice = filters.price?.$gte;
    const maxPrice = filters.price?.$lte;
    if (minPrice && maxPrice) return t('filterControls.priceRangeValue', { minPrice: formatCurrencyForDisplay(minPrice), maxPrice: formatCurrencyForDisplay(maxPrice) });
    if (minPrice) return t('filterControls.priceMin', { minPrice: formatCurrencyForDisplay(minPrice) });
    if (maxPrice) return t('filterControls.priceMax', { maxPrice: formatCurrencyForDisplay(maxPrice) });
    return t('filterControls.price');
  };

  const applyBedFilter = () => {
    const bedsFilterObject = {};
    if (tempBedMin) bedsFilterObject.$gte = tempBedMin;
    if (tempBedMax) bedsFilterObject.$lte = tempBedMax;

    const min = parseInt(tempBedMin, 10);
    const max = parseInt(tempBedMax, 10);

    if (tempBedMin && tempBedMax && !isNaN(min) && !isNaN(max) && max < min) {
      setBedFilterError(t('filterControls.bedError'));
      return;
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      beds: bedsFilterObject,
    }));
    setBedFilterError('');
    setActiveDropdown(null);
  };

  const clearBedFilter = () => {
    setTempBedMin('');
    setTempBedMax('');
    setBedFilterError('');
    setFilters((prevFilters) => ({
      ...prevFilters,
      beds: {},
    }));
  };

  const handleBedInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyBedFilter();
    }
  };

  const getBedsButtonLabel = () => {
    const minBeds = filters.beds?.$gte;
    const maxBeds = filters.beds?.$lte;
    if (minBeds && maxBeds) return t('filterControls.bedsRange', { minBeds, maxBeds });
    if (minBeds) return t('filterControls.bedsMin', { minBeds });
    if (maxBeds) return t('filterControls.bedsMax', { maxBeds });
    return t('filterControls.beds');
  };

  const handlePropertyTypeCheckboxChange = (event) => {
    const { value, checked } = event.target;
    setTempSelectedTypes((prevTypes) => (checked ? [...prevTypes, value] : prevTypes.filter((type) => type !== value)));
  };

  const applyPropertyTypeFilter = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      type: tempSelectedTypes,
    }));
    setActiveDropdown(null);
  };

  const clearPropertyTypeFilter = () => {
    setTempSelectedTypes([]);
    setFilters((prevFilters) => ({
      ...prevFilters,
      type: [],
    }));
  };

  const getPropertyTypeButtonLabel = () => {
    const numSelected = filters.type?.length || 0;
    if (numSelected === 0) return t('filterControls.type');
    if (numSelected === 1) return filters.type[0];
    if (numSelected === PropertyTypeOptions.length) return t('filterControls.allTypes');
    return t('filterControls.typeCount', { count: numSelected });
  };

  const handleDropdownKeyDown = (event, applyCallback) => {
    if (event.key === 'Enter') {
      applyCallback();
    }
  };

  const applyYearBuiltFilter = () => {
    const yearBuiltFilterObject = {};
    if (tempYearBuiltMin) yearBuiltFilterObject.$gte = tempYearBuiltMin;
    if (tempYearBuiltMax) yearBuiltFilterObject.$lte = tempYearBuiltMax;

    const min = parseInt(tempYearBuiltMin, 10);
    const max = parseInt(tempYearBuiltMax, 10);
    const currentYear = new Date().getFullYear();

    if (tempYearBuiltMin && (isNaN(min) || min < 1000 || min > currentYear)) {
      setYearBuiltFilterError(t('filterControls.yearBuiltErrorMin', { currentYear }));
      return;
    }
    if (tempYearBuiltMax && (isNaN(max) || max < 1000 || max > currentYear)) {
      setYearBuiltFilterError(t('filterControls.yearBuiltErrorMax', { currentYear }));
      return;
    }
    if (tempYearBuiltMin && tempYearBuiltMax && !isNaN(min) && !isNaN(max) && max < min) {
      setYearBuiltFilterError(t('filterControls.yearBuiltErrorRange'));
      return;
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      yearBuilt: yearBuiltFilterObject,
    }));
    setYearBuiltFilterError('');
    setActiveDropdown(null);
  };

  const clearYearBuiltFilter = () => {
    setTempYearBuiltMin('');
    setTempYearBuiltMax('');
    setYearBuiltFilterError('');
    setFilters((prevFilters) => ({
      ...prevFilters,
      yearBuilt: {},
    }));
  };

  const handleYearBuiltInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyYearBuiltFilter();
    }
  };

  const getYearBuiltButtonLabel = () => {
    const minYear = filters.yearBuilt?.$gte;
    const maxYear = filters.yearBuilt?.$lte;
    if (minYear && maxYear) return t('filterControls.yearBuiltRange', { minYear, maxYear });
    if (minYear) return t('filterControls.yearBuiltMin', { minYear });
    if (maxYear) return t('filterControls.yearBuiltMax', { maxYear });
    return t('filterControls.yearBuilt');
  };

  const handlePropertyStatusCheckboxChange = (event) => {
    const { value, checked } = event.target;
    const numericValue = parseInt(value, 10);
    setTempSelectedStatuses((prevStatuses) => (checked ? [...prevStatuses, numericValue] : prevStatuses.filter((status) => status !== numericValue)));
  };

  const applyStatusFilter = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      status: tempSelectedStatuses,
    }));
    setActiveDropdown(null);
  };

  const clearStatusFilter = () => {
    setTempSelectedStatuses([]);
    setFilters((prevFilters) => ({
      ...prevFilters,
      status: [],
    }));
  };

  const getPropertyStatusButtonLabel = () => {
    const numSelected = filters.status?.length || 0;
    const totalOptions = PropertyStatusOptions.filter((option) => option.value !== PropertyStatusNumeric.Hide).length;

    if (numSelected === 0) return t('filterControls.status');
    if (numSelected === 1) {
      const selectedOption = PropertyStatusOptions.find((option) => option.value === filters.status[0]);
      return selectedOption ? selectedOption.label : t('filterControls.status');
    }
    if (numSelected === totalOptions) return t('filterControls.allStatuses');
    return t('filterControls.statusCount', { count: numSelected });
  };

  const handleStateSelection = (stateAbbr) => {
    setTempSelectedState(stateAbbr);
    setFilters((prevFilters) => ({
      ...prevFilters,
      state: stateAbbr,
    }));
    setActiveDropdown(null);
  };

  const clearStateFilter = () => {
    setTempSelectedState('');
    // Just update the filter. The map component will react to this change.
    setFilters((prevFilters) => ({ ...prevFilters, state: '' }));
    setActiveDropdown(null);
  };

  const getStateButtonLabel = () => {
    if (filters.state) {
      const selectedStateObj = usStates.find((s) => s.value === filters.state);
      return selectedStateObj ? selectedStateObj.label : t('filterControls.state');
    }
    return t('filterControls.state');
  };

  const applyPropertyIDFilter = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      propertyID: tempPropertyID,
    }));
    setActiveDropdown(null);
  };

  const clearPropertyIDFilter = () => {
    setTempPropertyID('');
    setFilters((prevFilters) => ({
      ...prevFilters,
      propertyID: '',
    }));
  };

  const handlePropertyIDInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyPropertyIDFilter();
    }
  };

  const getPropertyIDButtonLabel = () => {
    return filters.propertyID ? t('filterControls.propertyIDWithValue', { id: filters.propertyID }) : t('filterControls.propertyID');
  };

  const applySellerUsernameFilter = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      sellerUsername: tempSellerUsername,
    }));
    setActiveDropdown(null);
  };

  const clearSellerUsernameFilter = () => {
    setTempSellerUsername('');
    setFilters((prevFilters) => ({
      ...prevFilters,
      sellerUsername: '',
    }));
  };

  const handleSellerUsernameInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applySellerUsernameFilter();
    }
  };

  const getSellerUsernameButtonLabel = () => {
    return filters.sellerUsername ? t('filterControls.sellerWithValue', { seller: filters.sellerUsername }) : t('filterControls.seller');
  };

  return (
    <div className={`filter-controls-container layout-${layout}`}>
      {showMapControls && (
        <div className="address-search-wrapper">
          <AddressSearchInput onLocationSelect={onLocationSelect} placeholder={t('filterControls.addressPlaceholder')} />
        </div>
      )}

      {/* State Filter Button and Dropdown */}
      <div className="filter-item-wrapper" ref={stateDropdownRef}>
        <button className={`filter-button ${activeDropdown === 'state' ? 'active' : ''} ${filters.state ? 'applied' : ''}`} onClick={() => toggleFilterDropdown('state')}>
          {getStateButtonLabel()}
        </button>
        {activeDropdown === 'state' && (
          <div className="filter-dropdown state-filter-dropdown">
            <div className="filter-dropdown-header">{t('filterControls.selectState')}</div>
            <div className="filter-dropdown-content">
              <div className="form-group state-options-list" ref={stateListRef}>
                {usStates.map((state) => (
                  <div key={state.value} className={`state-option-item ${tempSelectedState === state.value ? 'selected' : ''}`} onClick={() => handleStateSelection(state.value)}>
                    {state.label}
                  </div>
                ))}
              </div>
            </div>
            {filters.state && (
              <div className="filter-dropdown-actions">
                <button className="action-button clear-button" onClick={clearStateFilter}>
                  {t('filterControls.clearState')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Price Filter Button and Dropdown */}
      <div className="filter-item-wrapper" ref={priceDropdownRef}>
        <button className={`filter-button ${activeDropdown === 'price' ? 'active' : ''} ${filters.price?.$gte || filters.price?.$lte ? 'applied' : ''}`} onClick={() => toggleFilterDropdown('price')}>
          {getPriceButtonLabel()}
        </button>
        {activeDropdown === 'price' && (
          <div className="filter-dropdown price-filter-dropdown">
            <div className="filter-dropdown-header">{t('filterControls.priceRange')}</div>
            <div className="filter-dropdown-content">
              <div className="form-group price-inputs">
                <input
                  type="text"
                  placeholder={t('filterControls.min')}
                  value={getFormattedPriceForInput(tempPriceMin)}
                  onChange={(e) => handlePriceInputChange(e, setTempPriceMin)}
                  onKeyDown={handlePriceInputKeyDown}
                  aria-label={t('filterControls.minPriceAria')}
                />
                <span>-</span>
                <input
                  type="text"
                  placeholder={t('filterControls.max')}
                  value={getFormattedPriceForInput(tempPriceMax)}
                  onChange={(e) => handlePriceInputChange(e, setTempPriceMax)}
                  onKeyDown={handlePriceInputKeyDown}
                  aria-label={t('filterControls.maxPriceAria')}
                />
              </div>
              {priceFilterError && <div className="filter-error-message">{priceFilterError}</div>}
            </div>
            <div className="filter-dropdown-actions">
              <button className="action-button clear-button" onClick={clearPriceFilter}>
                {t('filterControls.clear')}
              </button>
              <button className="action-button apply-button" onClick={applyPriceFilter}>
                {t('filterControls.apply')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Beds Filter Button and Dropdown */}
      <div className="filter-item-wrapper" ref={bedsDropdownRef}>
        <button className={`filter-button ${activeDropdown === 'beds' ? 'active' : ''} ${filters.beds?.$gte || filters.beds?.$lte ? 'applied' : ''}`} onClick={() => toggleFilterDropdown('beds')}>
          {getBedsButtonLabel()}
        </button>
        {activeDropdown === 'beds' && (
          <div className="filter-dropdown beds-filter-dropdown">
            <div className="filter-dropdown-header">{t('filterControls.numBedrooms')}</div>
            <div className="filter-dropdown-content">
              <div className="form-group beds-inputs">
                <input type="number" placeholder={t('filterControls.min')} value={tempBedMin} onChange={(e) => setTempBedMin(e.target.value)} onKeyDown={handleBedInputKeyDown} aria-label={t('filterControls.minBedsAria')} />
                <span>-</span>
                <input type="number" placeholder={t('filterControls.max')} value={tempBedMax} onChange={(e) => setTempBedMax(e.target.value)} onKeyDown={handleBedInputKeyDown} aria-label={t('filterControls.maxBedsAria')} />
              </div>
              {bedFilterError && <div className="filter-error-message">{bedFilterError}</div>}
            </div>
            <div className="filter-dropdown-actions">
              <button className="action-button clear-button" onClick={clearBedFilter}>
                {t('filterControls.clear')}
              </button>
              <button className="action-button apply-button" onClick={applyBedFilter}>
                {t('filterControls.apply')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Property Type Filter Button and Dropdown */}
      <div className="filter-item-wrapper" ref={propertyTypeDropdownRef}>
        <button className={`filter-button ${activeDropdown === 'propertyType' ? 'active' : ''} ${(filters.type?.length || 0) > 0 ? 'applied' : ''}`} onClick={() => toggleFilterDropdown('propertyType')}>
          {getPropertyTypeButtonLabel()}
        </button>
        {activeDropdown === 'propertyType' && (
          <div className="filter-dropdown property-type-filter-dropdown" onKeyDown={(e) => handleDropdownKeyDown(e, applyPropertyTypeFilter)}>
            <div className="filter-dropdown-header">{t('filterControls.propertyType')}</div>
            <div className="filter-dropdown-content">
              <div className="form-group property-type-options">
                {PropertyTypeOptions.map((option) => (
                  <div key={option.value} className="checkbox-item">
                    <input type="checkbox" id={`type-${option.value}`} name={option.value} value={option.value} checked={tempSelectedTypes.includes(option.value)} onChange={handlePropertyTypeCheckboxChange} />
                    <label htmlFor={`type-${option.value}`}>{option.label}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="filter-dropdown-actions">
              <button className="action-button clear-button" onClick={clearPropertyTypeFilter}>
                {t('filterControls.clear')}
              </button>
              <button className="action-button apply-button" onClick={applyPropertyTypeFilter}>
                {t('filterControls.apply')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Year Built Filter Button and Dropdown */}
      <div className="filter-item-wrapper" ref={yearBuiltDropdownRef}>
        <button className={`filter-button ${activeDropdown === 'yearBuilt' ? 'active' : ''} ${filters.yearBuilt?.$gte || filters.yearBuilt?.$lte ? 'applied' : ''}`} onClick={() => toggleFilterDropdown('yearBuilt')}>
          {getYearBuiltButtonLabel()}
        </button>
        {activeDropdown === 'yearBuilt' && (
          <div className="filter-dropdown year-built-filter-dropdown">
            <div className="filter-dropdown-header">{t('filterControls.yearBuiltRangeLabel')}</div>
            <div className="filter-dropdown-content">
              <div className="form-group year-built-inputs">
                <input type="number" placeholder={t('filterControls.min')} value={tempYearBuiltMin} onChange={(e) => setTempYearBuiltMin(e.target.value)} onKeyDown={handleYearBuiltInputKeyDown} aria-label={t('filterControls.minYearAria')} />
                <span>-</span>
                <input type="number" placeholder={t('filterControls.max')} value={tempYearBuiltMax} onChange={(e) => setTempYearBuiltMax(e.target.value)} onKeyDown={handleYearBuiltInputKeyDown} aria-label={t('filterControls.maxYearAria')} />
              </div>
              {yearBuiltFilterError && <div className="filter-error-message">{yearBuiltFilterError}</div>}
            </div>
            <div className="filter-dropdown-actions">
              <button className="action-button clear-button" onClick={clearYearBuiltFilter}>
                {t('filterControls.clear')}
              </button>
              <button className="action-button apply-button" onClick={applyYearBuiltFilter}>
                {t('filterControls.apply')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Status Filter Button and Dropdown */}
      <div className="filter-item-wrapper" ref={statusDropdownRef}>
        <button className={`filter-button ${activeDropdown === 'status' ? 'active' : ''} ${(filters.status?.length || 0) > 0 ? 'applied' : ''}`} onClick={() => toggleFilterDropdown('status')}>
          {getPropertyStatusButtonLabel()}
        </button>
        {activeDropdown === 'status' && (
          <div className="filter-dropdown status-filter-dropdown" onKeyDown={(e) => handleDropdownKeyDown(e, applyStatusFilter)}>
            <div className="filter-dropdown-header">{t('filterControls.propertyStatus')}</div>
            <div className="filter-dropdown-content">
              <div className="form-group status-options">
                {PropertyStatusOptions.filter((option) => option.value !== PropertyStatusNumeric.Hide).map((option) => (
                  <div key={option.value} className="checkbox-item">
                    <input type="checkbox" id={`status-${option.value}`} name={option.label} value={option.value} checked={tempSelectedStatuses.includes(option.value)} onChange={handlePropertyStatusCheckboxChange} />
                    <label htmlFor={`status-${option.value}`}>{option.label}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="filter-dropdown-actions">
              <button className="action-button clear-button" onClick={clearStatusFilter}>
                {t('filterControls.clear')}
              </button>
              <button className="action-button apply-button" onClick={applyStatusFilter}>
                {t('filterControls.apply')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* PropertyID Filter Button and Dropdown */}
      <div className="filter-item-wrapper" ref={propertyIDDropdownRef}>
        <button className={`filter-button ${activeDropdown === 'propertyID' ? 'active' : ''} ${filters.propertyID ? 'applied' : ''}`} onClick={() => toggleFilterDropdown('propertyID')}>
          {getPropertyIDButtonLabel()}
        </button>
        {activeDropdown === 'propertyID' && (
          <div className="filter-dropdown">
            <div className="filter-dropdown-header">{t('filterControls.filterByPropertyID')}</div>
            <div className="filter-dropdown-content">
              <div className="form-group">
                <input type="text" placeholder={t('filterControls.enterPropertyID')} value={tempPropertyID} onChange={(e) => setTempPropertyID(e.target.value)} onKeyDown={handlePropertyIDInputKeyDown} aria-label={t('filterControls.propertyIDAria')} />
              </div>
            </div>
            <div className="filter-dropdown-actions">
              <button className="action-button clear-button" onClick={clearPropertyIDFilter}>
                {t('filterControls.clear')}
              </button>
              <button className="action-button apply-button" onClick={applyPropertyIDFilter}>
                {t('filterControls.apply')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* SellerUsername Filter Button and Dropdown (Admin Only) */}
      {showPropertiesControls && user.isAdmin && (
        <div className="filter-item-wrapper" ref={sellerUsernameDropdownRef}>
          <button className={`filter-button ${activeDropdown === 'sellerUsername' ? 'active' : ''} ${filters.sellerUsername ? 'applied' : ''}`} onClick={() => toggleFilterDropdown('sellerUsername')}>
            {getSellerUsernameButtonLabel()}
          </button>
          {activeDropdown === 'sellerUsername' && (
            <div className="filter-dropdown">
              <div className="filter-dropdown-header">{t('filterControls.filterBySeller')}</div>
              <div className="filter-dropdown-content">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder={t('filterControls.enterSeller')}
                    value={tempSellerUsername}
                    onChange={(e) => setTempSellerUsername(e.target.value)}
                    onKeyDown={handleSellerUsernameInputKeyDown}
                    aria-label={t('filterControls.sellerAria')}
                  />
                </div>
              </div>
              <div className="filter-dropdown-actions">
                <button className="action-button clear-button" onClick={clearSellerUsernameFilter}>
                  {t('filterControls.clear')}
                </button>
                <button className="action-button apply-button" onClick={applySellerUsernameFilter}>
                  {t('filterControls.apply')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Map-Specific Buttons */}
      {showMapControls && (
        <>
          {/* My Properties Button */}
          {user.isSeller && (
            <div className="filter-item-wrapper">
              <button className={`filter-button my-properties-button ${isMyPropertiesView ? 'applied' : ''}`} onClick={handleMyPropertiesToggle} title={isMyPropertiesView ? t('filterControls.viewingMyProperties') : t('filterControls.viewMyProperties')}>
                <FontAwesomeIcon icon={isMyPropertiesView ? faHomeSolid : faHomeRegular} />
              </button>
            </div>
          )}

          {/* My Saved Homes Button */}
          {user.isLoggedIn && (
            <div className="filter-item-wrapper">
              <button
                className={`filter-button saved-homes-button ${isSavedView ? 'applied' : ''}`}
                onClick={handleSavedHomesToggle}
                title={isSavedView ? t('filterControls.viewingSavedHomes') : t('filterControls.viewSavedHomes', { count: user.savedProperties?.length || 0 })}
              >
                <FontAwesomeIcon icon={isSavedView ? faHeartSolid : faHeartRegular} />
                {` (${user.savedProperties?.length || 0})`}
              </button>
            </div>
          )}
        </>
      )}

      {/* Clear All Filters Button */}
      {layout === 'horizontal' && isAnyFilterActive && (
        <div className="filter-item-wrapper">
          <button className="filter-button clear-all-button" onClick={handleClearAllFilters} title={t('filterControls.clearAllTitle')}>
            {t('filterControls.clear')}
          </button>
        </div>
      )}
    </div>
  );
});

export default FilterControls;
